import React from 'react';
import { PhaseState } from '../../types/session';
import strategyRegistry from '../../services/strategyRegistry';
import { useTheme } from '../../contexts/ThemeContext';

interface PhaseComponentFactoryProps {
  phase: string;
  state: PhaseState;
  onAction: (action: string, data?: any) => void;
}

export const PhaseComponentFactory: React.FC<PhaseComponentFactoryProps> = ({ phase, state, onAction }) => {
  console.log('PhaseComponentFactory props:', { phase, state });
  
  const { darkMode } = useTheme();
  const [PhaseComponent, setPhaseComponent] = React.useState<React.ComponentType<any> | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  
  React.useEffect(() => {
    let mounted = true;
    
    const loadComponent = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const component = await strategyRegistry.getComponent(phase);
        
        if (mounted) {
          setPhaseComponent(() => component);
          setLoading(false);
        }
      } catch (err) {
        console.error(`Error loading component for phase ${phase}:`, err);
        if (mounted) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      }
    };
    
    loadComponent();
    
    return () => {
      mounted = false;
    };
  }, [phase]);
  
  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-pulse">
          <div className={`h-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded w-1/4 mx-auto mb-4`}></div>
          <div className={`h-8 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded w-1/2 mx-auto mb-4`}></div>
          <div className={`h-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded w-3/4 mx-auto`}></div>
        </div>
        <p className={`mt-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading {phase} component...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={`text-center py-4 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
        <p>Error loading phase component: {error.message}</p>
        <button 
          className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          onClick={() => window.location.reload()}
        >
          Reload Page
        </button>
      </div>
    );
  }
  
  if (!PhaseComponent) {
    return (
      <div className={`text-center py-4 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
        No component found for phase: {phase}
      </div>
    );
  }
  
  try {
    console.log(`About to render ${phase} component:`, PhaseComponent);
    return (
      <PhaseComponent 
        phase={phase}
        state={state}
        onAction={onAction}
      />
    );
  } catch (renderError) {
    console.error('Error rendering phase component:', renderError);
    return (
      <div className={`text-center py-4 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
        <p>Error rendering phase component. See console for details.</p>
        <button 
          className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          onClick={() => window.location.reload()}
        >
          Reload Page
        </button>
      </div>
    );
  }
};
